import { MouseEvent, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useEvent, useUnit } from 'effector-react';
import { calculateContextMenuPosition, openContextMenuEvent } from '@nx-frontend/shared/ui/context-menu';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $userGranularPermissions } from '@nx-frontend/shared/store/user-granular-permissions';
import { $workspaceAccess, $wpCanManageMembers } from '@nx-frontend/shared/store/workspace-access';
import {
  ContextMenuItemData,
  ContextMenuItemType,
  PopupType,
  UiContextMenuConfigPosition,
  UiContextMenuTheme,
} from '@nx-frontend/shared/types';
import { getFilterMenuItemsByPrivileges } from '@nx-frontend/shared/utils';
import { deleteAgentThreadEvent } from './agent-store';

type AgentThreadContextMenuParams = {
  agentId: string;
  threadId: string;
  orgId: string;
};

export const useOpenMenuContextAgentThread = () => {
  const [unleashFlags, userGranularPermissions, workspaceAccess, wpCanManageMembers] = useUnit([
    $unleashFlags,
    $userGranularPermissions,
    $workspaceAccess,
    $wpCanManageMembers,
  ]);
  const [openContextMenu, openModal, closeModal, deleteAgentThread] = useEvent([
    openContextMenuEvent,
    openModalEvent,
    closeModalEvent,
    deleteAgentThreadEvent,
  ]);

  const t = useTranslations();

  return useCallback(
    async (params: AgentThreadContextMenuParams, e: MouseEvent, useMousePos?: boolean) => {
      const deleteItem: ContextMenuItemData = {
        type: ContextMenuItemType.HTML,
        id: 'delete',
        html: (
          <div className="text-red-500 flex items-center justify-start gap-2">
            <Icon icon={IconsMap.TRASH} className="w-4 h-4" />
            Delete
          </div>
        ),
        action: async () => {
          openModal({
            title: 'Delete agent',
            content: PopupType.DIALOG,
            showCloseButton: true,
            closeButtonSize: 18,
            payload: {
              payloadType: PopupType.DIALOG,
              text: 'Are you sure you want to delete this thread?',
              buttons: [
                {
                  text: t('sidebar.sidebar-cancel'),
                },
                {
                  text: t('sidebar.sidebar-context_menus.delete'),
                  main: true,
                  onClick: () => {
                    deleteAgentThread({
                      agentId: params.agentId,
                      threadId: params.threadId,
                      orgId: params.orgId,
                    });
                    closeModal();
                  },
                },
              ],
            },
          });
        },
      };

      const items: ContextMenuItemData[] = [deleteItem];

      const itemsFilteredByPriveleges = getFilterMenuItemsByPrivileges(
        wpCanManageMembers,
        workspaceAccess,
        userGranularPermissions,
        unleashFlags
      )(items);
      if (itemsFilteredByPriveleges.length !== 0) {
        openContextMenu({
          items: itemsFilteredByPriveleges,
          position: calculateContextMenuPosition(
            e.target,
            4,
            undefined,
            useMousePos ? { x: e.clientX, y: e.clientY } : undefined
          ),
          config: {
            position: UiContextMenuConfigPosition.BOTTOM_RIGHT,
            menuPadding: 0,
            theme: UiContextMenuTheme.LIGHT,
          },
        });
      }
    },
    [
      wpCanManageMembers,
      workspaceAccess,
      userGranularPermissions,
      unleashFlags,
      openModal,
      t,
      deleteAgentThread,
      closeModal,
      openContextMenu,
    ]
  );
};
